import React, { useState, useEffect } from 'react'
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Box,
  Typography,
  Chip,
  Checkbox,
  Divider,
  CircularProgress,
  Stack,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { MuiTelInput } from 'mui-tel-input'
import { User, Role, PermissionInfo, UserLanguage } from '../../types'
import { getUserPermissions, updateUserPermissions } from '../../services/users'

/** The app's supported languages — must match the API's SUPPORTED_LANGUAGES. */
const LANGUAGE_OPTIONS: Array<{ code: UserLanguage; flag: string; label: string }> = [
  { code: 'en', flag: '🇺🇸', label: 'English' },
  { code: 'es', flag: '🇪🇸', label: 'Español' },
  { code: 'he', flag: '🇮🇱', label: 'עברית' },
  { code: 'ar', flag: '🇸🇦', label: 'العربية' },
  { code: 'th', flag: '🇹🇭', label: 'ภาษาไทย' },
]

/**
 * Falls back to the admin's own UI language when creating a user, so onboarding
 * a team in one language doesn't mean re-picking it on every user.
 * @param uiLanguage - The admin console's active i18n language
 * @returns A supported language code
 */
function defaultLanguageFor(uiLanguage: string): UserLanguage {
  const base = uiLanguage.split('-')[0]
  const match = LANGUAGE_OPTIONS.find(option => option.code === base)
  return match ? match.code : 'en'
}

/**
 * User form props
 */
interface UserFormProps {
  user?: User | null
  onSubmit: (userData: Partial<User> & { password?: string }) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
  availableRoles: Role[]
  parentOptions?: Array<{ id: string; name: string }>
  showParentSelect?: boolean
}

/**
 * User form component with granular permissions management
 */
function UserForm({ user, onSubmit, onCancel, isLoading, availableRoles, parentOptions = [], showParentSelect }: UserFormProps): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [address, setAddress] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<Role>('user')
  const [isActive, setIsActive] = useState(true)
  const [parentAccountId, setParentAccountId] = useState('')
  const [language, setLanguage] = useState<UserLanguage>('en')

  // Permissions state
  const [useCustomPermissions, setUseCustomPermissions] = useState(false)
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])
  const [roleDefaults, setRoleDefaults] = useState<string[]>([])
  const [allPermissions, setAllPermissions] = useState<PermissionInfo[]>([])
  const [permissionsLoading, setPermissionsLoading] = useState(false)
  const [permissionsSaving, setPermissionsSaving] = useState(false)

  useEffect(() => {
    if (user) {
      setName(user.name)
      setEmail(user.email)
      setPhone(user.phone || '')
      setAddress(user.address || '')
      setRole(user.role)
      setIsActive(user.isActive)
      setParentAccountId(user.parentAccountId || '')
      setLanguage(user.settings?.language || 'en')
      setPassword('')
      loadPermissions(user.id)
    } else {
      setName('')
      setEmail('')
      setPhone('')
      setAddress('')
      setPassword('')
      setRole('user')
      setIsActive(true)
      setParentAccountId('')
      setLanguage(defaultLanguageFor(i18n.language))
      setUseCustomPermissions(false)
      setSelectedPermissions([])
      setRoleDefaults([])
      setAllPermissions([])
    }
  }, [user])

  async function loadPermissions(userId: string): Promise<void> {
    try {
      setPermissionsLoading(true)
      const res = await getUserPermissions(userId)
      const data = res.data
      setUseCustomPermissions(data.customPermissions)
      setSelectedPermissions(data.customPermissions ? data.permissions : data.roleDefaults)
      setRoleDefaults(data.roleDefaults)
      setAllPermissions(data.allPermissions)
    } catch (error) {
      console.error('Error loading permissions:', error)
    } finally {
      setPermissionsLoading(false)
    }
  }

  function togglePermission(permKey: string): void {
    if (!useCustomPermissions) return
    setSelectedPermissions(prev =>
      prev.includes(permKey) ? prev.filter(p => p !== permKey) : [...prev, permKey]
    )
  }

  function handleToggleCustom(custom: boolean): void {
    setUseCustomPermissions(custom)
    if (!custom) {
      setSelectedPermissions(roleDefaults)
    }
  }

  async function savePermissions(): Promise<void> {
    if (!user) return
    try {
      setPermissionsSaving(true)
      await updateUserPermissions(user.id, {
        customPermissions: useCustomPermissions,
        permissions: useCustomPermissions ? selectedPermissions : undefined,
      })
    } catch (error: any) {
      alert(error.response?.data?.error || t('userDetails.perms.saveError'))
    } finally {
      setPermissionsSaving(false)
    }
  }

  function getGroupedPermissions(): Record<string, PermissionInfo[]> {
    const grouped: Record<string, PermissionInfo[]> = {}
    for (const perm of allPermissions) {
      if (!grouped[perm.category]) {
        grouped[perm.category] = []
      }
      grouped[perm.category].push(perm)
    }
    return grouped
  }

  function buildUserPayload(includeParent?: boolean): Partial<User> & { password?: string } {
    const data: Partial<User> & { password?: string } = {
      name,
      email,
      phone: phone || undefined,
      address: address || undefined,
      role,
      isActive,
      settings: { language },
    }
    if (includeParent) data.parentAccountId = parentAccountId || undefined
    if (!user || password) data.password = password
    return data
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    await onSubmit(buildUserPayload(showParentSelect))
    if (user) {
      await savePermissions()
    }
  }

  const groupedPermissions = getGroupedPermissions()

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <TextField
        label={t('users.name')}
        value={name}
        onChange={e => setName(e.target.value)}
        required
        disabled={isLoading}
        size="small"
        fullWidth
      />

      <TextField
        label={t('users.email')}
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        required
        disabled={isLoading || !!user}
        size="small"
        fullWidth
      />

      <MuiTelInput
        label={t('users.phone')}
        value={phone}
        onChange={setPhone}
        defaultCountry="US"
        forceCallingCode
        size="small"
        fullWidth
        disabled={isLoading}
      />

      <TextField
        label={t('users.address')}
        value={address}
        onChange={e => setAddress(e.target.value)}
        disabled={isLoading}
        placeholder={t('users.addressPlaceholder')}
        size="small"
        fullWidth
      />

      {(!user || password) && (
        <TextField
          label={t('users.password')}
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required={!user}
          disabled={isLoading}
          inputProps={{ minLength: 6 }}
          size="small"
          fullWidth
        />
      )}

      <FormControl size="small" fullWidth>
        <InputLabel>{t('users.role')}</InputLabel>
        <Select
          value={role}
          onChange={e => setRole(e.target.value as Role)}
          label={t('users.role')}
          disabled={isLoading}
        >
          {availableRoles.map(r => (
            <MenuItem key={r} value={r}>
              {t(`users.roles.${r}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel id="user-language-label">{t('settings.language')}</InputLabel>
        <Select
          labelId="user-language-label"
          id="user-language"
          value={language}
          onChange={e => setLanguage(e.target.value as UserLanguage)}
          label={t('settings.language')}
          disabled={isLoading}
        >
          {LANGUAGE_OPTIONS.map(option => (
            <MenuItem key={option.code} value={option.code}>
              <span style={{ marginInlineEnd: 8 }}>{option.flag}</span>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showParentSelect && (
        <FormControl size="small" fullWidth>
          <InputLabel>{t('users.parentAccount')}</InputLabel>
          <Select
            value={parentAccountId}
            onChange={e => setParentAccountId(e.target.value)}
            label={t('users.parentAccount')}
            disabled={isLoading}
          >
            <MenuItem value="">{t('users.noParent')}</MenuItem>
            {parentOptions.map(option => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
            disabled={isLoading}
            color="success"
          />
        }
        label={t('users.isActive')}
      />

      {/* Permissions Section - only shown when editing an existing user */}
      {user && allPermissions.length > 0 && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">{t('permissions.title')}</Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                label={t('userDetails.perms.roleDefaults')}
                variant={!useCustomPermissions ? 'filled' : 'outlined'}
                color={!useCustomPermissions ? 'primary' : 'default'}
                onClick={() => handleToggleCustom(false)}
                size="small"
              />
              <Chip
                label={t('userDetails.perms.custom')}
                variant={useCustomPermissions ? 'filled' : 'outlined'}
                color={useCustomPermissions ? 'warning' : 'default'}
                onClick={() => handleToggleCustom(true)}
                size="small"
              />
            </Stack>
          </Box>

          {permissionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            Object.entries(groupedPermissions).map(([category, perms]) => (
              <Box key={category} sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="primary" fontWeight="bold" sx={{ mb: 0.5 }}>
                  {category}
                </Typography>
                <Divider sx={{ mb: 0.5 }} />
                {perms.map(perm => {
                  const isChecked = selectedPermissions.includes(perm.key)
                  const isRoleDefault = roleDefaults.includes(perm.key)
                  return (
                    <Box key={perm.key} sx={{ display: 'flex', alignItems: 'center', py: 0.25 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={isChecked}
                            onChange={() => togglePermission(perm.key)}
                            disabled={!useCustomPermissions || isLoading}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {perm.label}
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {perm.description}
                            </Typography>
                          </Typography>
                        }
                        sx={{ flex: 1, mr: 0 }}
                      />
                      {isChecked && (
                        <Chip
                          label={useCustomPermissions ? t('userDetails.perms.sourceCustom') : t('userDetails.perms.sourceRole')}
                          size="small"
                          color={!useCustomPermissions || isRoleDefault ? 'info' : 'warning'}
                          sx={{ fontSize: '10px', height: 20 }}
                        />
                      )}
                    </Box>
                  )
                })}
              </Box>
            ))
          )}

          {permissionsSaving && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="primary">{t('userDetails.perms.saving')}</Typography>
            </Box>
          )}
        </Box>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
        <Button type="submit" variant="contained" disabled={isLoading || permissionsSaving} fullWidth>
          {isLoading ? <CircularProgress size={20} /> : user ? t('button.save.text') : t('button.add.text')}
        </Button>
        <Button variant="outlined" onClick={onCancel} disabled={isLoading} fullWidth>
          {t('button.cancel.text')}
        </Button>
      </Stack>
    </Box>
  )
}

export default UserForm
